import { Search as SearchIcon } from 'lucide-react';
import logoLight from '../../../assets/branding/logo_horizontal_TextoAzulEscuro.svg';
import logoDark from '../../../assets/branding/logo_horizontal_TextoBranco.svg';
import { HomeIcon } from '../../../components/ui/icons/common/home';
import { api, urls } from '../../../services/api';
import { useEffect, useState } from 'react';
import { IMenuItem } from '../../../types/menu';
import { ItemMenuNav } from './components/ItemMenuNav';
import { useApp } from '../../../context/App';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';

interface Props {
	search?: boolean;
	dataSearch?: unknown[];
	onChange?: (array: unknown[]) => void;
	route?: string;
}

export function Header({ search = false, route }: Props) {
	const { configDomainURL, getTenantUrl, tenant } = useApp();
	const { isDark } = useTheme();
	const domain = configDomainURL;
	const [menu, setMenu] = useState<IMenuItem[]>([]);
	const [idMenu, setIdMenu] = useState<number>(0);
	const [inputSearch, setInputSearch] = useState<string>('');
	const location = useLocation();

	const logo = isDark ? logoDark : logoLight;

	useEffect(() => {
		(async () => {
			const response = await api.get(urls.menus);
			setMenu(response.data);
		})();
	}, []);

	const currentPath = location.pathname;
	const isNoticiasPage = currentPath === '/noticias/';
	const isNoticiasSubPage = currentPath.startsWith('/noticias/') && currentPath !== '/noticias/';

	const isDefaultTenant = tenant.id === 'municipio';

	return (
		<header className="w-full bg-white dark:bg-surface-dark transition-colors duration-300">
			{/* Main Header Row */}
			<div className={`flex items-center justify-center w-full ${!search ? '' : 'border-b border-border-default dark:border-border-default-dark'}`}>
				<div className="w-full lg:w-[85%] max-w-7xl flex flex-col lg:flex-row justify-between items-center py-4 lg:py-6">
					<div className="flex items-center gap-4 lg:gap-6 mb-4 lg:mb-0">
						<a href={getTenantUrl(domain)} className="transition-transform duration-300 hover:scale-105 shrink-0">
							<img src={logo} className="w-48 lg:w-56" alt="Prefeitura de Itajaí" />
						</a>

						{!isDefaultTenant && (
							<div className="flex items-center h-10 lg:h-12 border-l-2 border-primary/20 dark:border-white/20 pl-4 lg:pl-6">
								<h1 className="font-roboto font-black text-xl lg:text-3xl text-primary dark:text-white uppercase tracking-tighter leading-none italic">
									{tenant.name.replace('Portal da ', '').replace('Portal do ', '')}
								</h1>
							</div>
						)}
					</div>

					<div className="flex flex-col items-center lg:items-end w-full lg:w-auto">
						{search && (
							<form
								className="w-full lg:w-64 h-10 flex flex-row rounded-xl border border-border-default dark:border-border-default-dark bg-surface-alt dark:bg-surface-dark-alt mt-2 mb-4 overflow-hidden focus-within:ring-2 focus-within:ring-primary/20 dark:focus-within:ring-secondary/20 transition-all shadow-sm"
								action={getTenantUrl(`${domain}busca/${inputSearch}`)}
							>
								<input
									className="w-full bg-transparent border-none text-on-surface dark:text-on-surface-dark text-sm font-normal font-roboto pl-4 outline-none placeholder:text-on-surface-subtle dark:placeholder:text-on-surface-dark-subtle"
									placeholder="O que você busca?"
									onChange={(e) => setInputSearch(e.target.value)}
									aria-label="Campo de busca"
								/>
								<button
									className="h-full flex items-center justify-center bg-primary dark:bg-primary-light text-white border-none w-12 cursor-pointer transition-colors hover:bg-primary-dark dark:hover:bg-primary"
									type="submit"
									aria-label="Buscar"
								>
									<SearchIcon size={18} />
								</button>
							</form>
						)}

						<nav
							className="flex flex-wrap items-center justify-center lg:justify-end gap-2 lg:gap-3 uppercase text-xs lg:text-[0.8rem] font-bold font-roboto text-primary dark:text-on-surface-dark"
							aria-label="Navegação principal"
						>
							<a href={getTenantUrl(domain)} className="flex items-center gap-1.5 no-underline px-3 py-2 rounded-lg transition-all duration-200 hover:bg-primary/5 dark:hover:bg-white/5 group">
								<span className="[&>svg]:h-4 [&>svg]:fill-secondary group-hover:scale-110 transition-transform">
									<HomeIcon />
								</span>
								Início
							</a>

							{menu && menu.map((item: IMenuItem, index: number) => (
								<ItemMenuNav
									key={index}
									idActual={idMenu}
									itemMenu={item}
									onChange={setIdMenu}
									count={index + 1}
								/>
							))}
						</nav>
					</div>
				</div>
			</div>

			{/* Breadcrumbs Row */}
			{route && (
				<div className="w-full flex justify-center bg-surface-alt/50 dark:bg-surface-dark-alt/50 border-b border-border-default dark:border-border-default-dark">
					<div className="w-full lg:w-[85%] max-w-7xl py-2 px-4 text-[0.625rem] lg:text-xs font-semibold text-on-surface-subtle dark:text-on-surface-dark-muted uppercase">
						<div className="flex items-center gap-2">
							<a href={getTenantUrl("/")} className="hover:text-primary dark:hover:text-secondary transition-colors no-underline">Inicial</a>
							<span className="text-on-surface-subtle dark:text-on-surface-dark-subtle">/</span>

							{isNoticiasPage && (
								<span className="text-primary dark:text-on-surface-dark">Notícias</span>
							)}

							{isNoticiasSubPage && (
								<>
									<a href={getTenantUrl("/noticias")} className="hover:text-primary dark:hover:text-secondary transition-colors no-underline">Notícias</a>
									<span className="text-on-surface-subtle dark:text-on-surface-dark-subtle">/</span>
									<span className="text-primary dark:text-on-surface-dark truncate max-w-[12.5rem] lg:max-w-none">{route}</span>
								</>
							)}

							{!isNoticiasPage && !isNoticiasSubPage && route && (
								<span className="text-primary dark:text-on-surface-dark">{route}</span>
							)}
						</div>
					</div>
				</div>
			)}
		</header>
	);
}
